import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { Download, ExternalLink, FileText, Receipt, Truck } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { ARTIFACT_KINDS, type ArtifactKind } from './supply-order/types.js';

/** Dokumentu veidi sarakstā: dzīves cikla artefakti + klienta rēķins (`so/<id>/artifact/invoice/`). */
type DocKind = ArtifactKind | 'invoice';

interface ShippingDocRow {
  id: string;
  supplyOrderId: string;
  supplyOrderRef: string;
  supplyOrderStatus: string;
  kind: DocKind | null;
  type: string;
  filename: string;
  createdAt: string;
  retentionUntil: string | null;
}

interface ShippingDocList {
  items: ShippingDocRow[];
  paging: { limit: number; offset: number; nextOffset: number | null };
}

const KIND_TONE: Record<DocKind, BadgeTone> = {
  label: 'violet',
  cmr: 'blue',
  packing_list: 'teal',
  delivery_note: 'amber',
  driver_signed_cmr: 'green',
  receiver_signed_cmr: 'green',
  invoice: 'amber',
};

/** Veidu filtra pogas platformas/piegādātāja/pārvadātāja skatam (klientam filtru nav). */
const FILTER_KINDS: readonly DocKind[] = [...ARTIFACT_KINDS, 'invoice'];

/** Ģenerētie HTML dokumenti atveras jaunā cilnē; skenējumi (PDF/attēli) — lejupielāde. */
const OPENABLE_KINDS: ReadonlySet<DocKind> = new Set([
  'label',
  'cmr',
  'packing_list',
  'delivery_note',
  'invoice',
]);

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function shippingDocsQuery(kind: DocKind | '', q: string, offset: number): string {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (kind) params.set('kind', kind);
  if (q) params.set('q', q);
  return `/api/shipping-docs?${params.toString()}`;
}

/**
 * Dokumenti pāri piegādes pasūtījumiem (GET /api/shipping-docs, aktora tvērumā).
 * `variant='invoices'` — klienta lomas: API atdod TIKAI AmberNord rēķinus (lēmums 2026-09-04),
 * tāpēc veidu filtri un saite uz piegādes pasūtījumu (klientam nepieejams) netiek rādīti.
 */
function ShippingDocsSection({ variant }: { variant: 'shipping' | 'invoices' }) {
  const { t, i18n } = useTranslation('documents');
  const invoicesOnly = variant === 'invoices';
  const ns = invoicesOnly ? 'invoices' : 'shipping';
  const [kind, setKind] = useState<DocKind | ''>('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<ShippingDocRow[] | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });
  const kindLabel = (row: ShippingDocRow) =>
    row.kind ? t(`shipping.kinds.${row.kind}`) : t('shipping.other');

  useEffect(() => {
    const handle = setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setError(null);
    void (async () => {
      const res = await api.get<ShippingDocList>(shippingDocsQuery(kind, q, 0));
      if (!alive) return;
      if (!res.ok) {
        setError(t(`${ns}.loadError`));
        return;
      }
      setItems(res.data.items ?? []);
      setNextOffset(res.data.paging?.nextOffset ?? null);
    })();
    return () => {
      alive = false;
    };
  }, [kind, q, t, ns]);

  async function loadMore() {
    if (nextOffset === null) return;
    setLoadingMore(true);
    try {
      const res = await api.get<ShippingDocList>(shippingDocsQuery(kind, q, nextOffset));
      if (!res.ok) return;
      // Offset lapošana jaunākie-vispirms sarakstā: pa vidu ievietota rinda atkārtotos.
      setItems((prev) => {
        const seen = new Set((prev ?? []).map((r) => r.id));
        return [...(prev ?? []), ...(res.data.items ?? []).filter((r) => !seen.has(r.id))];
      });
      setNextOffset(res.data.paging?.nextOffset ?? null);
    } finally {
      setLoadingMore(false);
    }
  }

  const filtered = kind !== '' || q !== '';

  return (
    <section className="mb-10">
      <h2 className="mb-1 text-base font-medium">{t(`${ns}.heading`)}</h2>
      <p className="mb-3 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
        {t(`${ns}.intro`)}
      </p>

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {invoicesOnly ? (
          <div />
        ) : (
          <fieldset className="flex flex-wrap gap-2">
            <legend className="sr-only">{t('shipping.columns.kind')}</legend>
            <Button
              type="button"
              size="sm"
              variant={kind === '' ? 'secondary' : 'ghost'}
              aria-pressed={kind === ''}
              onClick={() => setKind('')}
            >
              {t('shipping.allKinds')}
            </Button>
            {FILTER_KINDS.map((k) => (
              <Button
                key={k}
                type="button"
                size="sm"
                variant={kind === k ? 'secondary' : 'ghost'}
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
              >
                {t(`shipping.kinds.${k}`)}
              </Button>
            ))}
          </fieldset>
        )}
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t(`${ns}.search`)}
          aria-label={t(`${ns}.search`)}
          className="sm:w-64"
        />
      </div>

      {error ? (
        <DataState loading={false} error={error} empty={false}>
          {null}
        </DataState>
      ) : items === null ? (
        <SkeletonRows rows={5} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={invoicesOnly ? <Receipt className="h-6 w-6" /> : <Truck className="h-6 w-6" />}
          title={filtered ? t(`${ns}.noMatch.title`) : t(`${ns}.empty.title`)}
          hint={filtered ? t(`${ns}.noMatch.hint`) : t(`${ns}.empty.hint`)}
        />
      ) : invoicesOnly ? (
        <TableShell>
          <THead>
            <tr>
              <Th>{t('invoices.columns.number')}</Th>
              <Th>{t('invoices.columns.ref')}</Th>
              <Th>{t('invoices.columns.status')}</Th>
              <Th>{t('invoices.columns.issued')}</Th>
              <Th className="text-right">
                <span className="sr-only">{t('invoices.open')}</span>
              </Th>
            </tr>
          </THead>
          <tbody>
            {items.map((row) => (
              <Tr key={row.id}>
                <Td className="font-medium text-slate-900 dark:text-white">
                  {row.filename.replace(/\.html$/, '')}
                </Td>
                <Td>{row.supplyOrderRef}</Td>
                <Td>
                  <StatusBadge
                    status={row.supplyOrderStatus}
                    label={statusLabel(row.supplyOrderStatus)}
                  />
                </Td>
                <Td>{formatDate(row.createdAt, i18n.language)}</Td>
                <Td className="text-right">
                  <span className="inline-flex items-center gap-3">
                    <a
                      href={`/api/shipping-docs/doc/${row.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-amber-700 hover:underline dark:text-amber-400"
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden />
                      {t('invoices.open')}
                    </a>
                    <a
                      href={`/api/shipping-docs/doc/${row.id}?format=pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-amber-700 hover:underline dark:text-amber-400"
                    >
                      <FileText className="h-4 w-4" aria-hidden />
                      {t('invoices.pdf')}
                    </a>
                  </span>
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      ) : (
        <>
          <TableShell>
            <THead>
              <tr>
                <Th>{t('shipping.columns.ref')}</Th>
                <Th>{t('shipping.columns.kind')}</Th>
                <Th>{t('shipping.columns.status')}</Th>
                <Th>{t('shipping.columns.created')}</Th>
                <Th>{t('shipping.columns.retention')}</Th>
                <Th className="text-right">
                  <span className="sr-only">{t('shipping.open')}</span>
                </Th>
              </tr>
            </THead>
            <tbody>
              {items.map((row) => {
                const openable = row.kind !== null && OPENABLE_KINDS.has(row.kind);
                return (
                  <Tr key={row.id}>
                    <Td className="font-medium text-slate-900 dark:text-white">
                      <Link to={`/supply-orders/${row.supplyOrderId}`} className="hover:underline">
                        {row.supplyOrderRef}
                      </Link>
                      <span className="mt-0.5 block truncate text-xs font-normal text-slate-500 dark:text-slate-400">
                        {row.filename}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={row.kind ? KIND_TONE[row.kind] : 'slate'}>
                        {kindLabel(row)}
                      </Badge>
                    </Td>
                    <Td>
                      <StatusBadge
                        status={row.supplyOrderStatus}
                        label={statusLabel(row.supplyOrderStatus)}
                      />
                    </Td>
                    <Td>{formatDate(row.createdAt, i18n.language)}</Td>
                    <Td>{formatDate(row.retentionUntil, i18n.language)}</Td>
                    <Td className="text-right">
                      <span className="inline-flex items-center gap-3">
                        <a
                          href={`/api/shipping-docs/doc/${row.id}`}
                          target={openable ? '_blank' : undefined}
                          rel={openable ? 'noreferrer' : undefined}
                          className="inline-flex items-center gap-1 font-medium text-amber-700 hover:underline dark:text-amber-400"
                        >
                          {openable ? (
                            <ExternalLink className="h-4 w-4" aria-hidden />
                          ) : (
                            <Download className="h-4 w-4" aria-hidden />
                          )}
                          {openable ? t('shipping.open') : t('shipping.download')}
                        </a>
                        {openable && (
                          <a
                            href={`/api/shipping-docs/doc/${row.id}?format=pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-medium text-amber-700 hover:underline dark:text-amber-400"
                          >
                            <FileText className="h-4 w-4" aria-hidden />
                            {t('shipping.pdf')}
                          </a>
                        )}
                      </span>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </TableShell>
          {nextOffset !== null && (
            <div className="mt-3 flex justify-center">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {t('shipping.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

interface CoaItem {
  id: string;
  batchId: string;
  specId: string;
  status: string;
  overallResult: string | null;
  issuedAt: string | null;
}

interface Batch {
  id: string;
  lotNo: string;
}

interface ResultRow {
  parameter: string;
  measured: string;
}

export default function Documents() {
  const { t, i18n } = useTranslation('documents');
  const { toast } = useToast();
  const { user } = useAuth();
  const isCustomer = user?.role.startsWith('customer_') ?? false;
  const [items, setItems] = useState<CoaItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchId, setBatchId] = useState('');
  const [specId, setSpecId] = useState('');
  const [rows, setRows] = useState<ResultRow[]>([{ parameter: '', measured: '' }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });

  async function reloadCoa() {
    const res = await api.get<{ items: CoaItem[] }>('/api/documents/coa');
    if (res.ok) setItems(res.data.items ?? []);
  }

  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await api.get<{ items: CoaItem[] }>('/api/documents/coa');
      if (!alive) return;
      if (!res.ok) {
        setError(t('loadError'));
        return;
      }
      setItems(res.data.items ?? []);
    })();
    void (async () => {
      const res = await api.get<{ items: Batch[] }>('/api/traceability/batches');
      if (!alive || !res.ok) return;
      const list = res.data.items ?? [];
      setBatches(list);
      if (list[0]) setBatchId(list[0].id);
    })();
    return () => {
      alive = false;
    };
  }, [t]);

  function updateRow(index: number, patch: Partial<ResultRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((prev) => (prev.length >= 2 ? prev : [...prev, { parameter: '', measured: '' }]));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!batchId) {
      setFormError(t('errors.selectBatch'));
      return;
    }
    if (!specId.trim()) {
      setFormError(t('errors.enterSpecId'));
      return;
    }
    const results = rows
      .filter((r) => r.parameter.trim() !== '' && r.measured.trim() !== '')
      .map((r) => ({ parameter: r.parameter.trim(), measured: Number(r.measured) }));
    if (results.length === 0) {
      setFormError(t('errors.addParameter'));
      return;
    }
    if (results.some((r) => !Number.isFinite(r.measured))) {
      setFormError(t('errors.measuredNumbers'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string }>('/api/documents/coa', {
        batchId,
        specId: specId.trim(),
        results,
      });
      if (!res.ok) {
        const code = res.data.error ?? '';
        const msg = t('errors.issueFailed', { code: code || res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setSpecId('');
      setRows([{ parameter: '', measured: '' }]);
      toast(t('toast.issued'));
      await reloadCoa();
    } catch {
      const msg = t('errors.issueFailedRetry');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppLayout title={t('title')}>
      <p className="mb-6 max-w-2xl text-sm text-slate-600 dark:text-slate-300">{t('intro')}</p>

      <ShippingDocsSection variant={isCustomer ? 'invoices' : 'shipping'} />

      <div className="grid gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="mb-3 text-base font-medium">{t('list.heading')}</h2>
          {error ? (
            <DataState loading={false} error={error} empty={false}>
              {null}
            </DataState>
          ) : items === null ? (
            <SkeletonRows rows={5} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-6 w-6" />}
              title={t('empty.title')}
              hint={t('empty.hint')}
            />
          ) : (
            <TableShell>
              <THead>
                <tr>
                  <Th>{t('list.columns.spec')}</Th>
                  <Th>{t('list.columns.status')}</Th>
                  <Th>{t('list.columns.result')}</Th>
                  <Th>{t('list.columns.issued')}</Th>
                  <Th>{t('list.columns.html')}</Th>
                </tr>
              </THead>
              <tbody>
                {items.map((c) => (
                  <Tr key={c.id}>
                    <Td className="font-medium text-slate-900 dark:text-white">{c.specId}</Td>
                    <Td>
                      <StatusBadge status={c.status} label={statusLabel(c.status)} />
                    </Td>
                    <Td>
                      {c.overallResult ? (
                        <StatusBadge
                          status={c.overallResult.toLowerCase()}
                          label={statusLabel(c.overallResult)}
                        />
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </Td>
                    <Td>{formatDate(c.issuedAt, i18n.language)}</Td>
                    <Td>
                      <a
                        href={`/api/documents/coa/${c.id}/html`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-amber-700 hover:underline dark:text-amber-400"
                      >
                        {t('list.view')}
                      </a>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </section>

        <section>
          <Card>
            <CardHeader title={t('form.heading')} />
            <CardBody>
              <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
                <Field label={t('form.batchLabel')} htmlFor="coa-batch">
                  <Select
                    id="coa-batch"
                    required
                    value={batchId}
                    onChange={(e) => setBatchId(e.target.value)}
                  >
                    {batches.length === 0 ? (
                      <option value="">{t('form.noBatches')}</option>
                    ) : (
                      batches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.lotNo}
                        </option>
                      ))
                    )}
                  </Select>
                </Field>
                <Field label={t('form.specLabel')} htmlFor="coa-spec">
                  <Textarea
                    id="coa-spec"
                    rows={2}
                    value={specId}
                    onChange={(e) => setSpecId(e.target.value)}
                  />
                </Field>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('form.parametersLabel')}
                  </span>
                  {rows.map((r, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and never reordered
                    <div key={i} className="flex gap-2">
                      <Input
                        type="text"
                        placeholder={t('form.parameterPlaceholder')}
                        value={r.parameter}
                        onChange={(e) => updateRow(i, { parameter: e.target.value })}
                        className="min-w-0 flex-1"
                      />
                      <Input
                        type="number"
                        step="any"
                        placeholder={t('form.valuePlaceholder')}
                        value={r.measured}
                        onChange={(e) => updateRow(i, { measured: e.target.value })}
                        className="w-24"
                      />
                    </div>
                  ))}
                  {rows.length < 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addRow}
                      className="self-start"
                    >
                      {t('form.addParameter')}
                    </Button>
                  )}
                </div>
                {formError && (
                  <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                    {formError}
                  </p>
                )}
                <Button type="submit" disabled={submitting || batches.length === 0}>
                  {submitting ? t('form.submitting') : t('form.submit')}
                </Button>
              </form>
            </CardBody>
          </Card>
        </section>
      </div>
    </AppLayout>
  );
}
